import {
  projectEntity,
  type ProjectTask,
  type ProjectTaskChecklistSummary,
} from "@/common/model/project/project-entity";

function cloneSections(value: any): projectEntity["sections"] {
  if (!Array.isArray(value)) {
    return [];
  }
  return JSON.parse(JSON.stringify(value)) as projectEntity["sections"];
}

/** Only fields present on the SignalR payload — leaves the rest of the ORM row untouched. */
export function buildProjectSignalrPatch(data: any): Partial<projectEntity> {
  const patch: Partial<projectEntity> = {};

  if ("title" in data) patch.title = String(data.title);
  if ("statusId" in data) patch.statusId = Number(data.statusId);
  if ("number" in data) patch.number = (data.number as number | null) ?? null;
  if ("numberWithPrefix" in data) {
    patch.numberWithPrefix = String(data.numberWithPrefix ?? "");
  }
  if ("leadTechnicianId" in data) {
    patch.leadTechnicianId = data.leadTechnicianId as number | null;
  }
  if ("dueDate" in data) {
    patch.dueDate =
      typeof data.dueDate === "string" && data.dueDate.length > 0
        ? data.dueDate
        : null;
  }
  if ("taskCompletionPercentage" in data) {
    patch.taskCompletionPercentage = data.taskCompletionPercentage as
      | number
      | null;
  }
  if ("tick" in data) patch.tick = Number(data.tick ?? 0);
  if ("recentActivity" in data) patch.recentActivity = data.recentActivity;
  if ("updatedOn" in data) {
    patch.updatedOn = data.updatedOn ?? null;
  }
  if ("updatedById" in data) {
    patch.updatedById = data.updatedById ?? null;
  }
  if (Array.isArray(data.sections)) {
    patch.sections = cloneSections(data.sections);
  }
  if ("sharedWithContactIds" in data) {
    patch.sharedWithContactIds = data.sharedWithContactIds ?? []
  }

  const statusDetails = data.statusDetails as any;
  if ("statusReason" in data || statusDetails) {
    patch.statusReason =
      typeof statusDetails?.reason === "string"
        ? statusDetails.reason
        : (data.statusReason as string | null) ?? null;
  }

  return patch;
}

function pickLatestIso(
  ...values: Array<string | null | undefined>
): string | null {
  let latest: string | null = null;
  let latestMs = Number.NEGATIVE_INFINITY;

  for (const value of values) {
    const iso = value;
    if (!iso) {
      continue;
    }
    const ms = Date.parse(iso);
    if (!Number.isNaN(ms) && ms >= latestMs) {
      latestMs = ms;
      latest = iso;
    }
  }

  return latest;
}

/** Project list `updatedOn` / `updatedById` from SignalR envelope + payload. */
export function pickProjectActivityPatch(
  ...sources: Array<any>
): Partial<Pick<projectEntity, "updatedOn" | "updatedById">> {
  let updatedOn: string | null = null;
  let updatedById: number | null | undefined;

  for (const source of sources) {
    if (source == null || typeof source !== "object") {
      continue;
    }

    updatedOn = pickLatestIso(
      updatedOn,
      source.updatedOn,
      source.recentActivity?.createdOn,
    );

    if (updatedById === undefined && "updatedById" in source) {
      updatedById = (source.updatedById as number | null) ?? null;
    }
  }

  const patch: Partial<Pick<projectEntity, "updatedOn" | "updatedById">> = {};
  if (updatedOn) {
    patch.updatedOn = updatedOn;
  }
  if (updatedById !== undefined) {
    patch.updatedById = updatedById;
  }
  return patch;
}

export function normalizeProjectForOrm(entityId: string, data: any): any {
  const statusDetails = data.statusDetails as any;

  return {
    id: entityId,
    sections: cloneSections(data.sections ?? []),
    number: data.number ?? null,
    numberWithPrefix: data.numberWithPrefix ?? "",
    serviceProviderId: data.serviceProviderId ?? 0,
    title: data.title ?? "",
    description: data.description ?? "",
    clientId: data.clientId ?? 0,
    clientLocationId: data.clientLocationId ?? 0,
    leadTechnicianId: data.leadTechnicianId as number | null,
    sharedWithContactIds: data.sharedWithContactIds ?? [],
    typeId: data.typeId ?? "",
    tagIds: data.tagIds ?? [],
    groupIds: data.groupIds ?? [],
    dueDate: data.dueDate ?? null,
    isActive: data.isActive ?? true,
    statusId: data.statusId ?? 0,
    statusReason:
      typeof statusDetails?.reason === "string"
        ? statusDetails.reason
        : data.statusReason ?? null,
    createdById: data.createdById ?? 0,
    createdOn: data.createdOn ?? "",
    updatedById: data.updatedById ?? null,
    updatedOn: data.updatedOn ?? null,
    recentActivity: data.recentActivity ?? null,
    tick: data.tick ?? 0,
    taskAssignees: data.taskAssignees ?? [],
    taskCompletionPercentage: data.taskCompletionPercentage as number | null,
  };
}

export async function syncProjectListToStore(
  apiRows: unknown[],
  reset: boolean,
): Promise<void> {
  const rows = apiRows.map((row) => {
    const record = row as { id?: string };
    const id = String(record.id);
    return normalizeProjectForOrm(id, row);
  });

  if (reset) {
    const newIds = new Set(rows.map((row) => String(row.id)));
    await projectEntity.delete((project) => {
      const id = String(project.id);
      return id.length > 0 && !newIds.has(id);
    });
  }

  if (rows.length > 0) {
    await projectEntity.insertOrUpdate({ data: rows });
  }
}

function readChecklistDataSummary(
  value: any,
): ProjectTaskChecklistSummary | null {
  if (value == null || typeof value !== "object") {
    return null;
  }
  const summary = value as ProjectTaskChecklistSummary;
  return {
    percentageOfCompletedChecklistItems: Number(
      summary.percentageOfCompletedChecklistItems,
    ),
    totalItems: Number(summary.totalItems),
    completedItems: Number(summary.completedItems),
  };
}

function mergeTaskFromSignalrPayload(
  existing: ProjectTask,
  taskData: any,
): ProjectTask {
  const merged: ProjectTask = { ...existing };

  if (taskData.title != null) {
    merged.title = String(taskData.title);
  }
  if (taskData.statusId != null) {
    merged.statusId = Number(taskData.statusId);
  }
  if ("leadTechnicianId" in taskData) {
    merged.leadTechnicianId = taskData.leadTechnicianId as number | null;
  }
  if (Array.isArray(taskData.secondaryTechnicianIds)) {
    merged.secondaryTechnicianIds = taskData.secondaryTechnicianIds as number[];
  }
  if ("dueDate" in taskData) {
    merged.dueDate =
      typeof taskData.dueDate === "string" && taskData.dueDate.length > 0
        ? taskData.dueDate
        : null;
  }
  if (taskData.priorityId != null) {
    merged.priorityId = Number(taskData.priorityId);
  }
  if (taskData.sortOrder != null) {
    merged.sortOrder = Number(taskData.sortOrder);
  }
  if ("recentActivity" in taskData) {
    merged.recentActivity = taskData.recentActivity ?? null;
  }
  const summary = readChecklistDataSummary(taskData.checklistDataSummary);
  if (summary) {
    merged.checklistDataSummary = summary;
  }

  return merged;
}

function mapSignalrTaskToProjectTask(data: any): ProjectTask {
  return {
    id: String(data.id),
    title: String(data.title),
    statusId: Number(data.statusId),
    leadTechnicianId: data.leadTechnicianId as number | null,
    secondaryTechnicianIds: Array.isArray(data.secondaryTechnicianIds)
      ? (data.secondaryTechnicianIds as number[])
      : [],
    dueDate:
      typeof data.dueDate === "string" && data.dueDate.length > 0
        ? data.dueDate
        : null,
    priorityId: Number(data.priorityId),
    sortOrder: Number(data.sortOrder),
    recentActivity: data.recentActivity ?? null,
    checklistDataSummary: readChecklistDataSummary(data.checklistDataSummary),
  };
}

export function mergeTaskIntoProjectSections(
  sections: projectEntity["sections"],
  taskData: any,
): projectEntity["sections"] {
  const sectionId = String(taskData.sectionId);
  const taskId = String(taskData.id);

  const nextSections = cloneSections(sections);
  const sectionIndex = nextSections.findIndex(
    (section) => section.id === sectionId,
  );

  if (sectionIndex < 0) {
    const tasks = [mapSignalrTaskToProjectTask(taskData)];
    nextSections.push({
      id: sectionId,
      title: taskData.sectionTitle,
      color: taskData.sectionColor,
      tasks,
      isActive: true,
    });
    return nextSections;
  }

  const section = nextSections[sectionIndex];
  const tasks = Array.isArray(section.tasks) ? [...section.tasks] : [];
  const idx = tasks.findIndex((task) => task.id === taskId);
  if (idx >= 0) {
    tasks[idx] = mergeTaskFromSignalrPayload(tasks[idx], taskData);
  } else {
    tasks.push(mapSignalrTaskToProjectTask(taskData));
  }
  tasks.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  nextSections[sectionIndex] = { ...section, tasks };
  return nextSections;
}
